import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { gql, useQuery } from '@apollo/client';
import { observer } from 'mobx-react';
import { Accordion, BtwButton, BtwText, Separator, Stack, } from '@marvelapp/ballpark-components';
import { ArrowTopRightIcon, BlockIcon, CheckRadioIcon, LoaderIcon, PauseIcon, } from '@marvelapp/ballpark-icons';
import { pluralize } from '@marvelapp/core';
import { StudyStatusEnum, } from '../../../__generated__/queryTypes';
import { useRecruitmentState } from '../RecruitmentContext';
const LAST_ORDER_DETAILS = gql `
  query getLastOrder($testUUID: String) {
    recruitmentDetails(
      testUUID: $testUUID
      statuses: [ACTIVE, COMPLETED, CANCELLED, PAUSED]
    ) {
      orders(first: 1) @connection(key: "recruitmentOrders") {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            pk
            participants
            minutesPerParticipant
            totalNumberOfCredits
            numFulfilled
            status
            studyType
          }
        }
      }
    }
  }
`;
export const LastOrderBlock = observer(function LastOrderBlock() {
    var _a, _b, _c;
    const { testUUID } = useRecruitmentState();
    const { data, loading } = useQuery(LAST_ORDER_DETAILS, {
        variables: { testUUID },
    });
    if (loading)
        return null;
    const lastRecruitmentOrder = (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.recruitmentDetails) === null || _a === void 0 ? void 0 : _a.orders) === null || _b === void 0 ? void 0 : _b.edges[0]) === null || _c === void 0 ? void 0 : _c.node;
    return lastRecruitmentOrder ? (_jsx(Accordion.Root, { className: "rounded-lg border border-gray-600/10 bg-gray-200/50", collapsible: true, "data-testid": "last-order-block", type: "single", children: _jsxs(Accordion.Item, { className: "data-[state=open]:pb-0", value: "previousOrder", children: [_jsx(Accordion.Trigger, { className: "  px-4 py-3 hover:data-[state=closed]:bg-transparent", children: _jsxs(Stack, { align: "center", direction: "row", justify: "between", children: [_jsxs(BtwText, { size: "xs", weight: "medium", variant: "primary", children: ["Last order (#", lastRecruitmentOrder.pk, ")"] }), _jsxs(Stack, { align: "center", direction: "row", gap: "2", children: [_jsx(StatusIcon, { status: lastRecruitmentOrder.status }), _jsxs(BtwText, { size: "xs", variant: "primary", children: [lastRecruitmentOrder.numFulfilled, "/", lastRecruitmentOrder.participants, " completed"] })] })] }) }), _jsx(Accordion.Content, { className: "bg-transparent px-4 pb-3 pt-1", children: _jsxs(Stack, { gap: "4", width: "full", children: [_jsxs(Stack, { gap: "1.5", width: "full", children: [_jsxs(Stack, { align: "center", direction: "row", justify: "between", width: "full", children: [_jsxs(BtwText, { size: "xs", className: "flex w-full flex-row items-center gap-1", children: [_jsx("span", { children: lastRecruitmentOrder.participants }), _jsx("span", { children: "\u00D7" }), _jsx("span", { children: lastRecruitmentOrder.minutesPerParticipant }), _jsx("span", { className: "shrink-0", children: `min ${pluralize(lastRecruitmentOrder.minutesPerParticipant, 'response')}` })] }), _jsx(CreditsPerMinute, { studyType: lastRecruitmentOrder.studyType })] }), _jsxs(Stack, { align: "center", direction: "row", justify: "between", width: "full", children: [_jsx(BtwText, { size: "xs", weight: "semibold", children: "Total" }), ' ', _jsxs(BtwText, { size: "xs", weight: "semibold", children: [lastRecruitmentOrder.totalNumberOfCredits, ' ', pluralize(lastRecruitmentOrder.minutesPerParticipant, 'credit')] })] })] }), _jsx(Separator, { className: "bg-gray-600/10", direction: "horizontal" }), _jsx(ViewAllOrdersCTA, {})] }) })] }) })) : (_jsx(ViewAllOrdersCTA, {}));
});
const StatusIcon = observer(function StatusIcon({ status, }) {
    switch (status) {
        case StudyStatusEnum.ACTIVE:
            return _jsx(LoaderIcon, { className: "size-4 text-gray-600" });
        case StudyStatusEnum.COMPLETED:
            return _jsx(CheckRadioIcon, { className: "size-4 text-green-600" });
        case StudyStatusEnum.CANCELLED:
            return _jsx(BlockIcon, { className: "size-4 text-red-600" });
        case StudyStatusEnum.PAUSED:
            return _jsx(PauseIcon, { className: "size-4 text-gray-600" });
        default:
            return null;
    }
});
const CreditsPerMinute = observer(function CreditsPerMinute({ studyType, }) {
    const { getCreditsPerMinuteByStudyType } = useRecruitmentState();
    const creditsPerMinute = getCreditsPerMinuteByStudyType(studyType);
    return (_jsxs(BtwText, { align: "right", className: "w-full", size: "xs", children: [creditsPerMinute, " ", pluralize(creditsPerMinute, 'credit'), "/minute"] }));
});
const ViewAllOrdersCTA = observer(function ViewAllOrdersCTA() {
    return (_jsx(BtwButton, { asChild: true, "data-testid": "view-all-orders-cta", size: "intermediate", trailingIcon: _jsx(ArrowTopRightIcon, {}), width: "full", variant: "ghost", children: _jsx("a", { href: "/user/account/recruitment-orders", children: "View all previous orders" }) }));
});
